import React, {useMemo, useRef, useReducer, useState, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {orderBy} from 'lodash';

import {FilterConfiguration, FilterTypes} from '@ey/canvascoreservices/Filters';

import {labels, currentResource} from '../../../../../../util/utils';
import {MessageLogFilterTypeQuery} from '../../../../../../util/uiconstants';

import {MessageLogFilterEntityConfiguration, MessageLogFilterEntityTypes} from '../../../../../Common/Filter/config';

import getFilterCount from './utils/getFilterCount';

import Filters from './MessageLogFilterButton';
import MessageLogFilterControls from './MessageLogFilterControls';

export const MessageLogFilter = (props) => {
	const dispatchAction = useDispatch();
	const project = useSelector((state) => state.project);
	const displayedMessages = useSelector(({messageLog}) => messageLog.displayedMessages);
	const entities = useSelector((store) => store.contacts.entities);

	const [numberOfChips, setNumberOfChips] = useState(4);

	const getNumberOfChips = (w) => {
		if (w >= 1800) return 7;
		if (w >= 1600) return 6;
		if (w >= 1400) return 5;
		return 4;
	};

	useEffect(() => {
		const handleResize = () => {
			const {innerWidth: width} = window;
			setNumberOfChips(getNumberOfChips(width));
		};

		const {innerWidth: width} = window;
		setNumberOfChips(getNumberOfChips(width));

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const {actions, location} = props;

	const renderFilters = {};

	const RenderToolbar = {
		filters: {
			renderItem: (props) => (
				<Filters
					project={project}
					filterConfiguration={props.filterConfiguration}
					state={props.state}
					setState={props.setState}
					history={props.history}
					dispatch={props.dispatch}
					_syncQueryString={props._syncQueryString}
				/>
			),
			onRender: (args, renderProps) => {
				const projectId = location.search.split('projectId=')[1].substring(0, 36);

				const filterKey = `${projectId}#MessageLog`;

				const getFilterEntityByRedux = (filterItemName, entityType, entityFilter) => {
					if (filterItemName === MessageLogFilterTypeQuery.bySentBy) {
						return getSentBy(filterItemName, entityType, entityFilter, displayedMessages);
					}

					if (filterItemName === MessageLogFilterTypeQuery.byFromEntity) {
						return getFromEntity(filterItemName, entityType, entityFilter, displayedMessages, entities);
					}

					if (filterItemName === MessageLogFilterTypeQuery.byToEntity) {
						return getToEntity(filterItemName, entityType, entityFilter, displayedMessages, entities);
					}

					if (filterItemName === MessageLogFilterTypeQuery.byToCountry) {
						return getToCountry(filterItemName, entityType, entityFilter, displayedMessages);
					}

					if (filterItemName === MessageLogFilterTypeQuery.byToGroupRole) {
						return getToGroupRole(filterItemName, entityType, entityFilter, displayedMessages);
					}

					if (filterItemName === MessageLogFilterTypeQuery.byToEngagement) {
						return getToEngagement(filterItemName, entityType, entityFilter, displayedMessages);
					}

					if (filterItemName === MessageLogFilterTypeQuery.byToServiceLine) {
						return getToServiceLine(filterItemName, entityType, entityFilter, displayedMessages);
					}

					if (filterItemName === MessageLogFilterTypeQuery.byToRole) {
						return getToRole(filterItemName, entityType, entityFilter, displayedMessages);
					}
				};

				const entityFilter = {
					projectId: projectId
				};

				const getSentBy = (filterItemName, entityType, entityFilter, displayedMessages) => {
					let senders = displayedMessages?.map((message) => {
						return {
							id: message.sentByUserId,
							name: message.sentBy,
							firstName: message.createdUserFirstName,
							lastName: message.createdUserLastName
						};
					});

					senders = orderBy(senders, (sender) => [sender.firstName, senders.lastName]);

					let messageSentBy = removeDuplicates(senders);

					return messageSentBy;
				};

				const getFromEntity = (filterItemName, entityType, entityFilter, displayedMessages, entities) => {
					let messagesFrom = displayedMessages?.map((message) => {
						return {
							id: message.from.entityId,
							name: message.from.entityName
						};
					});

					let messageFromEntity = removeDuplicates(messagesFrom);
					let sortMessageFromEntity = sortEntities(entities, messageFromEntity);

					return sortMessageFromEntity;
				};

				const getToEntity = (filterItemName, entityType, entityFilter, displayedMessages, entities) => {
					let messagesEntities = displayedMessages
						?.flatMap((message) => message.entities ?? [])
						?.map((entity) => {
							return {
								id: entity.entityId,
								name: entity.entityName
							};
						});

					let messageToEntity = removeDuplicates(messagesEntities);
					let sortMessageToEntity = sortEntities(entities, messageToEntity);

					return sortMessageToEntity;
				};

				const getToCountry = (filterItemName, entityType, entityFilter, displayedMessages) => {
					let messagesCountries = displayedMessages?.flatMap((message) => message.countries ?? []);
					let messageToCountry = removeDuplicates(messagesCountries);
					messageToCountry = orderBy(messageToCountry, (country) => country.label);

					return messageToCountry;
				};

				const getToGroupRole = (filterItemName, entityType, entityFilter, displayedMessages) => {
					let messagesGroupRoles = displayedMessages?.flatMap((message) => message.groupRoles ?? []);
					let messageToGroupRoles = removeDuplicates(messagesGroupRoles);
					messageToGroupRoles = orderBy(messageToGroupRoles, (groupRole) => groupRole.id);

					return messageToGroupRoles;
				};

				const getToEngagement = (filterItemName, entityType, entityFilter, displayedMessages) => {
					let messagesEngagements = displayedMessages?.flatMap((message) => message.engagements ?? []);
					let messageToEngagements = removeDuplicates(messagesEngagements);
					messageToEngagements = orderBy(messageToEngagements, (engagement) => engagement.label);

					return messageToEngagements;
				};

				const getToServiceLine = (filterItemName, entityType, entityFilter, displayedMessages) => {
					let messagesServiceLine = displayedMessages?.flatMap((message) => message.serviceLine ?? []);
					let messageToServiceLines = removeDuplicates(messagesServiceLine);
					messageToServiceLines = orderBy(messageToServiceLines, (serviceLine) => serviceLine.label);

					return messageToServiceLines;
				};

				const getToRole = (filterItemName, entityType, entityFilter, displayedMessages) => {
					let messagesRoles = displayedMessages?.flatMap((message) => message.roles ?? []);
					let messageToRoles = removeDuplicates(messagesRoles);
					messageToRoles = orderBy(messageToRoles, (role) => role.label);

					return messageToRoles;
				};

				const removeDuplicates = (source) => {
					let results = [];
					Array.from(new Set(source?.map((data) => data.id))).map((id) => {
						if (id !== undefined) {
							results.push({
								value: id,
								label: source.find((e) => e.id === id).name
							});
						}
					});

					return results;
				};

				const sortEntities = (orderedEntities, entitiesToOrder) => {
					let sortedEntities = [];
					orderedEntities?.map((entity) => {
						if (entitiesToOrder?.find((x) => x.value === entity.entityId)) {
							sortedEntities.push({
								value: entity.entityId,
								label: entity.label
							});
						}
					});

					return sortedEntities;
				};

				// Calculate filterConfiguration just once and pass it down to renderItem function later
				renderProps.filterConfiguration = new FilterConfiguration(filterKey, {
					messageLogByMessageType: {
						label: labels.messageTypeFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						data: currentResource.MessageType,
						getDisplayValue: MessageLogFilterEntityConfiguration.messageType.getDisplayValue
					},
					messageLogBySentBy: {
						label: labels.sentByFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: MessageLogFilterEntityTypes.sentBy,
						entityFilter: MessageLogFilterEntityConfiguration.sentBy.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							MessageLogFilterTypeQuery.bySentBy,
							MessageLogFilterEntityTypes.sentBy,
							entityFilter
						),
						getDisplayValue: MessageLogFilterEntityConfiguration.sentBy.getDisplayValue
					},
					messageLogByFromEntity: {
						label: labels.fromEntityFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: MessageLogFilterEntityTypes.fromEntity,
						entityFilter: MessageLogFilterEntityConfiguration.fromEntity.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							MessageLogFilterTypeQuery.byFromEntity,
							MessageLogFilterEntityTypes.fromEntity,
							entityFilter
						),
						getDisplayValue: MessageLogFilterEntityConfiguration.fromEntity.getDisplayValue
					},
					messageLogByToEntity: {
						label: labels.toEntityFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: MessageLogFilterEntityTypes.toEntity,
						entityFilter: MessageLogFilterEntityConfiguration.toEntity.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							MessageLogFilterTypeQuery.byToEntity,
							MessageLogFilterEntityTypes.toEntity,
							entityFilter
						),
						getDisplayValue: MessageLogFilterEntityConfiguration.toEntity.getDisplayValue
					},
					messageLogByToCountry: {
						label: labels.toCountryFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: MessageLogFilterEntityTypes.toCountry,
						entityFilter: MessageLogFilterEntityConfiguration.toCountry.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							MessageLogFilterTypeQuery.byToCountry,
							MessageLogFilterEntityTypes.toCountry,
							entityFilter
						),
						getDisplayValue: MessageLogFilterEntityConfiguration.toCountry.getDisplayValue
					},
					messageLogByToGroupRole: {
						label: labels.toGroupRoleFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: MessageLogFilterEntityTypes.toGroupRole,
						entityFilter: MessageLogFilterEntityConfiguration.toGroupRole.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							MessageLogFilterTypeQuery.byToGroupRole,
							MessageLogFilterEntityTypes.toGroupRole,
							entityFilter
						),
						getDisplayValue: MessageLogFilterEntityConfiguration.toGroupRole.getDisplayValue
					},
					messageLogByToEngagement: {
						label: labels.toEngagementFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: MessageLogFilterEntityTypes.toEngagement,
						entityFilter: MessageLogFilterEntityConfiguration.toEngagement.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							MessageLogFilterTypeQuery.byToEngagement,
							MessageLogFilterEntityTypes.toEngagement,
							entityFilter
						),
						getDisplayValue: MessageLogFilterEntityConfiguration.toEngagement.getDisplayValue
					},
					messageLogByToServiceLine: {
						label: labels.toServiceLineFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: MessageLogFilterEntityTypes.toServiceLine,
						entityFilter: MessageLogFilterEntityConfiguration.toServiceLine.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							MessageLogFilterTypeQuery.byToServiceLine,
							MessageLogFilterEntityTypes.toServiceLine,
							entityFilter
						),
						getDisplayValue: MessageLogFilterEntityConfiguration.toServiceLine.getDisplayValue
					},
					messageLogByToRole: {
						label: labels.toRoleFilter,
						type: FilterTypes.search,
						searchPlaceholder: labels.search,
						noResultsPlaceholder: labels.noResultsAvailable,
						clearHoverText: labels.clearHoverText,
						entityType: MessageLogFilterEntityTypes.toRole,
						entityFilter: MessageLogFilterEntityConfiguration.toRole.entityFilter(entityFilter),
						data: getFilterEntityByRedux(
							MessageLogFilterTypeQuery.byToRole,
							MessageLogFilterEntityTypes.toRole,
							entityFilter
						),
						getDisplayValue: MessageLogFilterEntityConfiguration.toRole.getDisplayValue
					}
				});
			}
		},
		filterChips: {
			renderItem: (props) => (
				<MessageLogFilterControls
					key={props.key}
					project={project}
					filterConfiguration={props.filterConfiguration}
					state={props.state}
					setState={props.setState}
					history={props.history}
					dispatch={props.dispatch}
					numberOfChips={numberOfChips}
					_syncQueryString={props._syncQueryString}
				/>
			)
		}
	};

	const toolbar = [
		{
			className: 'filters',
			components: [RenderToolbar.filters]
		},
		{
			className: 'filter-chips',
			components: [RenderToolbar.filterChips]
		}
	];

	const [renderProps, filterKey] = useMemo(() => {
		let renderProps, filterKey;

		if (toolbar?.length > 0) {
			renderProps = {
				project
			};

			for (let i = 0; i < toolbar.length; i++) {
				const row = toolbar[i];

				for (let k = 0; k < row.components.length; k++) {
					const component = row.components[k];

					if (component.onRender) {
						component.onRender(
							// renderFilters object is mutable and might modified onRender
							{
								actions,
								location,
								project,
								renderFilters,
								filterKey: component.filterKey,
								history: props.history
							},

							// renderProps object is mutable and might modified onRender
							renderProps
						);
					}
				}
			}

			filterKey = renderProps.filterConfiguration?.key;
		}

		return [renderProps, filterKey];
	}, [displayedMessages]);

	const reducer = (state, action) => {
		switch (action.type) {
			case 'state/set': {
				return {
					...state,
					...action.value
				};
			}

			default:
				return state;
		}
	};

	let initialState = {};
	renderProps._syncQueryString = useRef(renderProps.filterConfiguration ? true : undefined);

	const [state, dispatch] = useReducer(reducer, initialState);
	return (
		<>
			{toolbar.map((row, index) => (
				<div
					key={`${index}`}
					className={row.className}
					style={row.style}
					numberOfChips={numberOfChips}
					numberOfSelectedChips={getFilterCount(state.filters)}
				>
					{row.components &&
						row.components.map((component, index) =>
							component.renderItem({
								key: `${index}`,
								...renderProps,
								history: props.history,
								state,
								dispatch
							})
						)}
				</div>
			))}
		</>
	);
};

export default withRouter(MessageLogFilter);
